import { useCallback, useEffect, useState } from "react";
import { Loader2, Inbox, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../api";
import IntakeStatusChip from "./intake/IntakeStatusChip";
import IntakeComposer from "./intake/IntakeComposer";
import IntakeQuestions from "./intake/IntakeQuestions";
import type { IntakeOut } from "./intake/types";

// Rohsprache-Intake -> Board (Harness-v3 C4). Der User dumpt Rohsprache, der
// Planner-Agent stellt Interview-Fragen (status='questions'), der User
// beantwortet sie (-> 'answered'), am Ende hängen Links auf die erzeugten
// Tasks/Phasen ('planned').
//
// Composer/StatusChip/Questions leben in ./intake/* als Shared-Komponenten,
// damit die Kanban-„Ingest"-Spalte dieselbe Logik ohne Duplikat nutzen kann.

export default function ProjectIntake({ projectId }: { projectId: string }) {
  const [intakes, setIntakes] = useState<IntakeOut[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setIntakes(await api.get<IntakeOut[]>(`/projects/${projectId}/intakes`));
    } catch {
      setIntakes([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      {/* Composer */}
      <IntakeComposer projectId={projectId} onCreated={load} />

      {/* Liste */}
      {loading && (
        <div className="text-sm text-text-2 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> lädt…
        </div>
      )}
      {!loading && intakes.length === 0 && (
        <div className="bg-surface border border-border rounded-md p-8 text-center text-sm text-text-2">
          <Inbox className="w-8 h-8 text-text-3 mx-auto mb-2" />
          Noch keine Intakes. Dumpe oben deine Rohsprache — der Planner wandelt
          sie in board-fertige Tasks um.
        </div>
      )}
      <div className="space-y-2">
        {intakes.map((it) => (
          <IntakeCard key={it.id} intake={it} onChanged={load} />
        ))}
      </div>
    </div>
  );
}

function IntakeCard({
  intake,
  onChanged,
}: {
  intake: IntakeOut;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(intake.status === "questions");

  const firstLine = (intake.raw_text || "").split("\n")[0].slice(0, 120);
  const when = new Date(intake.created_at).toLocaleString("de-DE");

  return (
    <div className="bg-surface border border-border rounded-md">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 text-text-3 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-text-3 shrink-0" />
        )}
        <span className="flex-1 min-w-0 truncate text-sm text-text">{firstLine || "(leer)"}</span>
        <IntakeStatusChip status={intake.status} />
        <span className="text-[10px] text-text-3 shrink-0">{when}</span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3 border-t border-border pt-3">
          <div className="text-xs text-text-2 whitespace-pre-wrap leading-relaxed">
            {intake.raw_text}
          </div>
          <IntakeQuestions intake={intake} onAnswered={onChanged} />
        </div>
      )}
    </div>
  );
}
